/**
 * Both storage purges are RESUMABLE: the backend stops at a deadline (kept under the
 * ALB idle timeout) and reports `done: false`, expecting the client to call again.
 * This is the loop that does that.
 *
 * Pure and side-effect-free apart from the injected `fn`, so it can be unit-tested
 * without rendering — `StorageUsageView` pulls in the router + the confirm context
 * and isn't jest-loadable (the `flowVolumeGeometry.js` / `processMonitorGroups.js`
 * precedent).
 */

/** Bytes/objects a pass reported, under either endpoint's field names. */
function passTotals(result) {
  const r = result || {};
  return {
    objects: r.purgedObjects ?? r.deletedObjects ?? 0,
    bytes: r.freedBytes ?? 0,
    records: r.purgedRecords ?? 0,
  };
}

/**
 * Call `fn` until it reports `done`, accumulating totals across passes.
 *
 * `maxPasses` is a runaway guard, not a budget: hitting it returns `done: false`,
 * which callers MUST surface as "partially freed — run again" rather than reporting
 * a completed purge.
 *
 * @param {() => Promise<object>} fn        one purge request
 * @param {(progress: object) => void} [onProgress] called after each pass
 * @returns {Promise<{objects:number, bytes:number, records:number, passes:number, done:boolean}>}
 */
export async function drainDelete(fn, onProgress, { maxPasses = 50 } = {}) {
  const totals = { objects: 0, bytes: 0, records: 0, passes: 0, done: false };
  while (totals.passes < maxPasses) {
    const result = await fn();
    const pass = passTotals(result);
    totals.objects += pass.objects;
    totals.bytes += pass.bytes;
    totals.records += pass.records;
    totals.passes += 1;
    totals.done = result?.done !== false; // a route without `done` is single-pass
    if (onProgress) onProgress({ ...totals });
    if (totals.done) break;
  }
  return totals;
}
